<template>
  <div>
    <template v-if="crmAvailable">
      <card-minimal-title class="m-t-2" :title="`${total_customers} customers`" />
      <table-list>
        <template v-slot:table-header>
          <div class="list-header p-a-md-3 ai-md-c">
            <div class="only-mobile filter d-f">
              <Button icon="sliders-24" :class="`ml-md-a small no-border-radius filter-button keep-hover blue f-1 no-padding only-icon ${mobileFilterOpen ? '' : 'only-text'}`" @click="mobileFilterOpen = true" :badge="filter.filters.length"></Button>
            </div>
            <div class="only-desktop f-1">
              <Button icon="sliders-24" :class="`ml-md-a medium minimal-border-radius keep-hover blue with-extra-side-padding ${filterOpen ? '' : 'only-text'}`" @click="filterOpen = !filterOpen" :badge="filter.filters.length">Filters</Button>
            </div>
            <div class="search ml-md-a mr-md-a f-md-1-5">
              <form-input-text class="f-w" icon="search-magnify-24" id="search" ref="search" placeholder="Search customers, broadcasts, giveaways" :value="searchString" @input="search($event)">
                <template slot="search-results">
                  <ul class="filter-search">
                    <template v-if="searchString !== ''">
                      <!-- Broadcasts row -->
                      <template v-if="searchBroadcasts.length > 0">
                        <li class="search-result-header">Broadcasts</li>
                        <li v-for="broadcast in searchBroadcasts" :key="broadcast.id" @click="addToFilter('broadcast_id', { value: broadcast.id, string: broadcast.metadata.title })">
                          <i class="icon-stream-24" />
                          {{ broadcast.metadata.title }}
                          <span>#{{ broadcast.id }}</span>
                        </li>
                      </template>
                      <template v-if="searchGiveaways.length > 0">
                        <li class="search-result-header">Giveaways</li>
                        <li v-for="giveaway in searchGiveaways" :key="giveaway.id" @click="addToFilter('giveaway_id', { value: giveaway.id, string: giveaway.title })">
                          <i class="icon-gift-24" />
                          {{ giveaway.title }}
                          <span>#{{ giveaway.id }}</span>
                        </li>
                      </template>
                      <!-- Customer row -->
                      <template v-if="searchCustomerResult">
                        <li class="search-result-header">Customer</li>
                        <li @click="addToFilter('email', { value: searchCustomers[0].email, string: searchCustomers[0].email })">
                          <i class="icon-user-24" />
                          {{ searchCustomers[0].email }}
                        </li>
                      </template>
                      <template v-if="!searchCustomerResult && searchBroadcasts.length === 0">
                        <p>No hits so far</p>
                      </template>
                      <template v-if="searchForms.length > 0">
                        <li class="search-result-header">Forms</li>
                        <li v-for="form in searchForms" :key="form.id" @click="addToFilter('form_id', { value: form.id, string: form.title })">
                          <i class="icon-edit-pen2" />
                          {{ form.title }}
                          <span>#{{ form.id }}</span>
                        </li>
                      </template>
                    </template>
                    <template v-else>
                      <template v-if="suggestedBroadcasts.length > 0">
                        <li class="search-result-header">Previous broadcasts</li>
                        <li v-for="broadcast in suggestedBroadcasts" :key="broadcast.id" @click="addToFilter('broadcast_id', { value: broadcast.id, string: broadcast.metadata.title })">
                          <i class="icon-stream-24" />
                          {{ broadcast.metadata.title }}
                          <span>#{{ broadcast.id }}</span>
                        </li>
                      </template>
                    </template>
                  </ul>
                </template>
              </form-input-text>
            </div>
            <div class="only-mobile buttons f-w d-f">
              <div class="button">
                <Button :class="`f-w medium only-text keep-hover no-border-radius padding ${allChecked ? 'red' : 'blue'}`" @click="checkAll()">{{ allChecked ? 'Unselect all' : 'Select all' }}</Button>
              </div>
              <div class="button">
                <Button class="f-w medium only-text keep-hover blue no-border-radius padding" @click="mobileSortOpen = true">Sort…</Button>
              </div>
              <div class="button">
                <Button icon="trash-24" class="f-w small only-text only-icon no-border-radius keep-hover red padding" :disabled="checkedCustomers.length === 0 && !allChecked" @click="deleteCustomers"></Button>
              </div>
              <div class="button">
                <Button icon="accounting-24" class="f-w small only-text only-icon no-border-radius keep-hover blue padding" @click="exportCustomers"></Button>
              </div>
            </div>
            <div class="only-desktop d-f f-1">
              <div class="button ml-a">
                <Button icon="trash-24" class="ml-md-a medium only-text keep-hover red minimal-border-radius with-extra-side-padding" :disabled="checkedCustomers.length === 0 && !allChecked" @click="deleteCustomers">Delete</Button>
              </div>
              <div class="spacer"></div>
              <div class="button">
                <Button icon="accounting-24" class="medium only-text keep-hover blue minimal-border-radius with-extra-side-padding" @click="exportCustomers">Export</Button>
              </div>
            </div>
          </div>
          <template v-if="filterOpen">
            <list-filter
              class="only-desktop"
              :giveaway="true"
              :filter="filter"
              @removeFilter="removeFromFilter"
            />
          </template>
          <portal to="modals">
            <modal :show="mobileFilterOpen" @close="mobileFilterOpen = false" class="auto-height" title="Filter customers">
              <list-filter
                :giveaway="true"
                :filter="filter"
                @removeFilter="removeFromFilter"
              />
            </modal>
            <modal :show="mobileSortOpen" @close="mobileSortOpen = false" class="auto-height" title="Sort customers">
              <div class="">
                <div class="p-a-4 d-f ai-c" style="border-bottom: 1px solid #f1f1f1;" @click="sortOn('created', 'desc')">
                  <i class="icon-checkbox-thin" :style="`cursor: pointer; color: #0060FF; font-size: 18px; margin-right: 6px; ${sort === 'created' && direction === 'desc' ? '' : 'opacity: 0;'}`" />
                  <p>Created <small class="large">Descending</small></p>
                </div>
                <div class="p-a-4 d-f ai-c" style="border-bottom: 1px solid #f1f1f1;" @click="sortOn('created', 'asc')">
                  <i class="icon-checkbox-thin" :style="`cursor: pointer; color: #0060FF; font-size: 18px; margin-right: 6px; ${sort === 'created' && direction === 'asc' ? '' : 'opacity: 0;'}`" />
                  <p>Created <small class="large">Ascending</small></p>
                </div>
                <div class="p-a-4 d-f ai-c" style="border-bottom: 1px solid #f1f1f1;" @click="sortOn('last_activity', 'desc')">
                  <i class="icon-checkbox-thin" :style="`cursor: pointer; color: #0060FF; font-size: 18px; margin-right: 6px; ${sort === 'last_activity' && direction === 'desc' ? '' : 'opacity: 0;'}`" />
                  <p>Last activity <small class="large">Descending</small></p>
                </div>
                <div class="p-a-4 d-f ai-c" style="border-bottom: 1px solid #f1f1f1;" @click="sortOn('last_activity', 'asc')">
                  <i class="icon-checkbox-thin" :style="`cursor: pointer; color: #0060FF; font-size: 18px; margin-right: 6px; ${sort === 'last_activity' && direction === 'asc' ? '' : 'opacity: 0;'}`" />
                  <p>Last activity <small class="large">Ascending</small></p>
                </div>
              </div>
            </modal>
          </portal>
        </template>
        <tr class="header only-desktop">
          <th class="no-padding-right w-0">
            <FormCheckbox class="" icon="check" @input="checkAll()" :selected="allChecked" />
          </th>
          <th class="w-100p w-md-50p">E-mail</th>
          <th class="w-25p sortable" :data-sorting-on="sort === 'created'" :data-sorting-direction="direction" @click="sortOn('created')">Created</th>
          <th class="w-25p sortable" :data-sorting-on="sort === 'last_activity'" :data-sorting-direction="direction" @click="sortOn('last_activity')">Last activity</th>
        </tr>
        <template v-if="customers && customers.length > 0">
          <!-- eslint-disable-next-line -->
          <a
            :href="`/customer/${customer.id}`"
            :key="customer.id"
            @click.prevent="handleLink($event, `/customer/${customer.id}`)"
            v-for="customer in customers"
          >
            <td class="no-padding-right w-0p" @click.prevent.stop="checkCustomer(customer.id)">
              <FormCheckbox class="no-event" icon="check" :selected="checkedCustomers.includes(customer.id)" />
            </td>
            <td class="strong w-100p w-md-50p ellipsis">
              <div class="blue" v-if="!customer.opted_out">{{ customer.email }}</div>
              <div class="gray" v-else>User has opted out</div>
              <div class="only-mobile">
                <div class="d-f f-wrap gray customer-dates p-t-1">
                  <div class="d-f ai-c m-r-2">
                    <i class="icon-clock-fill-24" /> <span class="tt-c">{{ getCreatedAt(customer.created_at) }}</span>
                  </div>
                  <div class="d-f ai-c">
                    <i class="icon-clock-fill-24" /> <span class="tt-c">{{ getCreatedAt(customer.last_activity) }}</span>
                  </div>
                </div>
              </div>
            </td>
            <td class="only-desktop">
              {{ getCreatedAt(customer.created_at) }}
            </td>
            <td class="only-desktop">
              <template v-if="customer.last_activity">
                {{ getCreatedAt(customer.last_activity) }}
              </template>
              <template v-else>
                N/A
              </template>
            </td>
          </a>
        </template>
        <template v-else>
          <a class="empty-list">
            <td colspan="4" class="p-a-4">
              No customers
            </td>
          </a>
        </template>
      </table-list>
      <pagination :page="current_page" :maxPage="max_page" @page="paginate" class="m-t-4 m-b-2" />
    </template>
  </div>
</template>

<script>

import moment from 'moment';
import { validateEmail, exportToCsv } from '@/utils';

import TableList from '@/components/Lists/Table.vue';
import Pagination from '@/components/Lists/Pagination.vue';
import ListFilter from '@/components/Customers/ListFilter.vue';

export default {
  components: {
    Pagination,
    TableList,
    ListFilter,
  },
  computed: {
    /**
     * Return the current tab
     * @return {String}
     */
    currentTab() {
      const tab = this.$route.path.split('/').pop();
      if (tab === 'customers') {
        return '';
      }

      return tab;
    },
    lvsEnabled() {
      return this.featuresEnabled.includes('lvs');
    },
    crmAvailable() {
      return this.accountSettings?.subscription?.crm;
    },
    searchCustomerResult() {
      if (validateEmail(this.searchString)) {
        if (this.searchCustomers.length > 0) {
          return this.searchCustomers[0];
        }
      }
      return null;
    },
  },
  methods: {
    addToFilter(type, value) {
      const filterIndex = this.filter.filters.findIndex(f => f.type === type);

      if (filterIndex >= 0) {
        this.filter.filters.splice(filterIndex, 1);
      }

      this.filter.filters.push({ type, value });

      this.searchString = '';
      this.$refs.search.clear();

      this.$router.push({
        path: this.$route.path,
        query: {
          ...this.$route.query,
          [type]: value.value === value.string ? value.value : `${value.value};${value.string}`,
        },
      });

      this.filterOpen = true;
    },
    removeFromFilter(filter) {
      const filterIndex = this.filter.filters.findIndex(f => f.type === filter.type);
      if (filterIndex >= 0) {
        this.filter.filters.splice(filterIndex, 1);

        const query = Object.assign({}, this.$route.query);
        delete query[filter.type];
        this.$router.replace({ query });
      }
    },
    getCreatedAt(date) {
      return Math.abs(moment().diff(date, 'days')) < 2 ? moment(date).from(moment()) : this.$options.filters.formatdate(date, 'MMM Do YYYY');
    },
    paginate(page) {
      this.current_page = page;

      this.$router.push({
        path: this.$route.path,
        query: {
          ...this.$route.query,
          page,
        },
      });

      this.getCustomers();
    },
    sortOn(type, forceDirection) {
      if (forceDirection) {
        this.direction = forceDirection;
      } else {
        // eslint-disable-next-line
        if (this.sort === type) {
          this.direction = this.direction === 'desc' ? 'asc' : 'desc';
        } else {
          this.direction = 'desc';
        }
      }

      this.sort = type;

      this.$router.push({
        path: this.$route.path,
        query: {
          ...this.$route.query,
          sort: type,
          direction: this.direction,
        },
      });

      this.getCustomers();

      this.mobileSortOpen = false;
    },
    /**
     * Add customer id to checkedCustomers array for export/deletion etc.
     * @param {String} id
     */
    checkCustomer(id) {
      const index = this.checkedCustomers.indexOf(id);

      if (index >= 0) {
        this.checkedCustomers.splice(index, 1);

        if (this.allChecked) {
          this.allChecked = false;
        }

        return;
      }

      this.checkedCustomers.push(id);
    },
    checkAll() {
      this.allChecked = !this.allChecked;
      this.checkedCustomers = this.allChecked ? this.customers.map(customer => customer.id) : [];
    },
    /**
     * Export customers
     * @returns {Void}
     */
    async exportCustomers() {
      const data = await this.$store.dispatch('Customers/exportCustomers', this.allChecked ? null : this.checkedCustomers);
      if (!data) {
        swal({ title: 'An error occured', text: 'Could not export customer(s)', icon: 'error' });
        return;
      }

      exportToCsv('customers_export.csv', data);
    },
    async deleteCustomers() {
      const willDelete = await swal({
        title: this.$t('errors.are_you_sure'), text: this.allChecked ? 'This will remove ALL customers' : 'This will remove checked customers', icon: 'warning', buttons: true, dangerMode: true,
      });

      if (!willDelete) {
        return;
      }

      const deleted = await this.$store.dispatch('Customers/deleteCustomers', this.allChecked ? null : this.checkedCustomers);
      if (!deleted) {
        swal({ title: 'An error occured', text: 'Could not delete customer(s)', icon: 'error' });
      }
      this.getCustomers();
    },
    async search(text) {
      this.searchString = text;

      clearTimeout(this.searchTimeout);

      this.searchTimeout = setTimeout(async () => {
        if (text === '') {
          this.searchCustomers = [];
          this.searchBroadcasts = [];
          this.searchGiveaways = [];
          return;
        }

        // Search broadcasts by title
        const payload = {
          page: 1,
          per_page: 3,
          type: 'past',
          title: `${text}`,
          skipConf: true,
          skipLoading: true,
          skipState: true,
        };
        const { broadcasts } = await this.$store.dispatch('Broadcasts/getBroadcasts', payload);
        this.searchBroadcasts = broadcasts;

        // Search customer email
        const {
          customers,
        } = await this.$store.dispatch('Customers/getCustomers', {
          email: text,
        });

        // Search giveaways
        const giveaways = await this.$store.dispatch('Giveaway/getList', {
          page: 1,
          per_page: 3,
          title: text,
        });

        const forms = await this.$store.dispatch('Forms/getAllForms', {
          page: 1,
          per_page: 3,
          title: text,
        });

        this.searchGiveaways = giveaways || [];

        this.searchCustomers = customers || [];

        this.searchForms = forms ? forms.forms : [];
      }, 250);
    },
    async getCustomers() {
      if (!this.crmAvailable) {
        return;
      }

      const {
        filter: {
          activity: {
            type: activityType,
            from: activityFrom,
            to: activityTo,
          },
          created: {
            type: createdType,
            from: createdFrom,
            to: createdTo,
          },
          filters,
          giveaway,
        },
      } = this;

      const payload = {
        page: this.current_page,
        per_page: this.per_page,
        sort: this.sort,
        direction: this.direction,
      };

      filters.forEach((filter) => {
        payload[filter.type] = filter.value.value;
      });

      giveaway.forEach((g) => {
        if (g.selected) {
          payload[g.type] = g.value;
        }
      });

      if (activityType === 'specific') {
        payload.last_activity_start = activityFrom;
        payload.last_activity_end = moment(activityTo).add(1, 'day').format('YYYY-MM-DD');
      }

      if (createdType === 'specific') {
        payload.created_start = createdFrom;
        payload.created_end = moment(createdTo).add(1, 'day').format('YYYY-MM-DD');
      }

      const {
        customers,
        total,
        pagination: {
          current_page = 1,
          max_page = 1,
        } = {},
      } = await this.$store.dispatch('Customers/getCustomers', payload);

      this.customers = customers;
      this.current_page = current_page;
      this.max_page = max_page;
      this.total_customers = total;
    },
    async getGiveaways() {
      return [];
    },
  },
  watch: {
    currentTab(tab) {
      switch (tab) {
        case 'form':
          this.getForms();
          break;
        default:
          this.getCustomers();
      }
    },
    filter: {
      deep: true,
      handler(filter) {
        const query = Object.assign({}, this.$route.query);

        filter.giveaway.forEach((g) => {
          if (g.selected) {
            query[g.type] = g.value;
          } else {
            delete query[g.type];
          }
        });

        if (filter.created.type === 'all') {
          delete query.created;
        } else {
          query.created = `${filter.created.from};${filter.created.to}`;
        }

        if (filter.activity.type === 'all') {
          delete query.activity;
        } else {
          query.activity = `${filter.activity.from};${filter.activity.to}`;
        }

        const validFilters = ['giveaway_id', 'broadcast_id', 'email'];
        const unusedFilters = validFilters.filter(vf => filter.filters.map(f => (f.type)).indexOf(vf) === -1);
        unusedFilters.forEach(uf => delete query[uf]);

        // eslint-disable-next-line
        this.$router.replace({ query }).catch(err => {});

        this.getCustomers();
      },
    },
  },
  data() {
    return {
      contactSales: false,
      checkedCustomers: [],
      allChecked: false,
      customers: [],
      total_customers: 0,
      max_page: 1,
      current_page: 1,
      per_page: 20,
      sort: 'created',
      direction: 'desc',
      suggestedBroadcasts: [],
      searchBroadcasts: [],
      searchGiveaways: [],
      searchForms: [],
      searchString: '',
      mobileFilterOpen: false,
      mobileSortOpen: false,
      filterOpen: false,
      searchCustomers: [],
      filter: {
        filters: [],
        giveaway: [
          // eslint-disable-next-line
          { type: 'giveaway_winner', value: true, string: 'Winners', selected: false },
          // eslint-disable-next-line
          { type: 'opted_out', value: true, string: 'Opted out', selected: false },
        ],
        created: {
          type: 'all',
          from: moment().subtract(6, 'months').format('YYYY-MM-DD'),
          to: moment().format('YYYY-MM-DD'),
        },
        activity: {
          type: 'all',
          from: moment().subtract(6, 'months').format('YYYY-MM-DD'),
          to: moment().format('YYYY-MM-DD'),
        },
      },
      searchTimeout: null,
    };
  },
  async mounted() {
    if (!this.lvsEnabled) {
      this.$router.replace('/dashboard');
      return;
    }

    const {
      page = 1,
      sort = 'created',
      direction = 'desc',
    } = this.$queryHelper.get({ validSort: ['created', 'id', 'updated', 'last_activity', 'opted_out'] });

    const {
      broadcast_id = '',
      giveaway_id = '',
      created = '',
      activity = '',
      opted_out = '',
      giveaway_winner,
      email = '',
    } = this.$route.query;

    this.current_page = page;
    this.sort = sort;
    this.direction = direction;

    const giveawayArr = [];
    if (opted_out) {
      giveawayArr.push('opted_out');
    }

    if (giveaway_winner) {
      giveawayArr.push('giveaway_winner');
    }

    this.filter.giveaway.forEach((g) => {
      if (giveawayArr.includes(g.type)) {
        // eslint-disable-next-line
        g.selected = true;
      }
    });

    if (broadcast_id !== '') {
      const broadcastSplit = broadcast_id.split(';');
      this.filter.filters.push({ type: 'broadcast_id', value: { value: broadcastSplit[0], string: broadcastSplit[1] } });
    }

    if (giveaway_id !== '') {
      const giveawaySplit = giveaway_id.split(';');
      this.filter.filters.push({ type: 'giveaway_id', value: { value: giveawaySplit[0], string: giveawaySplit[1] } });
    }

    if (activity !== '') {
      const last_activitySplit = activity.split(';');
      this.filter.activity = {
        type: 'specific',
        from: last_activitySplit[0],
        to: last_activitySplit[1],
      };
    }

    if (created !== '') {
      const user_createdSplit = created.split(';');
      this.filter.created = {
        type: 'specific',
        from: user_createdSplit[0],
        to: user_createdSplit[1],
      };
    }

    if (email !== '') {
      this.filter.filters.push({ type: 'email', value: { value: email, string: email } });
    }

    await this.getCustomers();

    // Search thingy
    const payload = {
      page: 1,
      per_page: 3,
      type: 'past',
      direction: 'desc',
      details: 'shopping',
      skipConf: true,
      skipLoading: true,
      skipState: true,
    };
    const { broadcasts } = await this.$store.dispatch('Broadcasts/getBroadcasts', payload);

    this.suggestedBroadcasts = broadcasts;
  },
  beforeDestroy() {
    clearTimeout(this.searchTimeout);
  },
};
</script>

<style lang="scss" scoped>
.customer-dates {
  i {
    font-size: 18px;
  }

  span {
    display: inline-block;
    padding-left: 4px;
    font-size: 12px;
  }
}

.filter-search {
  li {
    align-items: center;
    position: relative;
    display: flex;

    i {
      color: #868B97;
      position: absolute;
      font-size: 24px;
      left: 10px;
      top: 50%;
      transform: translateY(-50%);
    }

    span {
      color: rgb(108, 108, 108);
      font-family: Menlo, Consolas, monospace;
      margin-left: auto;
      font-size: 10px;
    }

    &:hover:not(.search-result-header) {
      background-color: #0060FF;
      color: white;
      cursor: pointer;

      i {
        color: white;
      }

      span {
        color: rgb(209, 209, 209);
      }
    }

    &:last-child {
      border-radius: 0 0 20px 20px;
    }
  }
}

.list-header {
  display: flex;
  flex-wrap: wrap;

  .spacer {
    background-color: rgba(0, 0, 0, 0.1);
    width: 1px;
    margin: 0 5px;
    height: 100%;
  }

  @media (max-width: 767px) {
    .filter {
      border-bottom: 1px solid #d8dae1;
      width: 60px;

      .filter-button {
        border-radius: 10px 0 0 0;
      }
    }

    .search {
      width: calc(100% - 60px);

      .input {
        border-radius: 0;
        border-top: 0;
        border-right: 0;
      }
    }

    .buttons {
      & > * {
        border-bottom: 1px solid #d8dae1;
        flex: 1;

        &:not(:last-child) {
          border-right: 1px solid #d8dae1;
        }
      }
    }
  }
}
</style>
